import { CommonModule, DatePipe, TitleCasePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  EditForm,
  EntityType,
  Report,
  ReportEntityDetails,
  Reporter,
  StatusOptions,
  UserInfo,
  UserReportFiles,
} from '../../../../shared/interfaces/report-content';
import { TimeAgoPipe } from '../../../../shared/pipes/time-ago.pipe';

@Component({
  selector: 'app-user-flag-details',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe, TitleCasePipe, TimeAgoPipe],
  templateUrl: './user-flag-details.component.html',
  styleUrl: './user-flag-details.component.scss',
})
export class UserFlagDetailsComponent {
  @Input({ required: true }) report!: Report;
  @Input() isManageFlags = false;
  @Input() isEditMode = false;
  @Input() editForm: EditForm = {
    severity: null,
    status: null,
    resolution_notes: '',
  };
  @Input() entityDetails: ReportEntityDetails | null = null;

  @Output() openFile = new EventEmitter<UserReportFiles>();
  @Output() downloadFile = new EventEmitter<UserReportFiles>();
  @Output() openEntityFile = new EventEmitter<void>();
  @Output() downloadEntityFile = new EventEmitter<void>();
  @Output() viewRecord = new EventEmitter<void>();

  readonly entityType = EntityType;
  readonly editableStatusOptions = StatusOptions.filter(
    (s) => s.value !== null,
  );

  get entityDetailsTitle(): string {
    switch (this.report?.entity_type) {
      case EntityType.BUSINESS:
        return 'Reported Business Details';
      case EntityType.FILE:
        return 'Reported File Details';
      case EntityType.NOTE:
        return 'Reported Note Details';
      case EntityType.NOTIFICATION:
        return 'Reported Notification Details';
      case EntityType.EMAIL:
        return 'Reported Email Details';
      default:
        return 'Reported Entity Details';
    }
  }

  getUserFullName(user: UserInfo | undefined | null): string {
    if (!user) {
      return '';
    }
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.first_name || user.last_name || user.username || '';
  }

  getReporterFullName(reporter: Reporter | undefined | null): string {
    if (!reporter) {
      return '';
    }
    if (reporter.first_name && reporter.last_name) {
      return `${reporter.first_name} ${reporter.last_name}`;
    }
    return reporter.first_name || reporter.last_name || reporter.email || '';
  }

  getSeverityClass(severity: number | string | null | undefined): string {
    if (typeof severity === 'number') {
      switch (severity) {
        case 1:
          return 'severity-low';
        case 2:
          return 'severity-medium';
        case 3:
          return 'severity-high';
        case 4:
          return 'severity-critical';
        default:
          return '';
      }
    }
    switch (severity?.toLowerCase()) {
      case 'low':
        return 'severity-low';
      case 'medium':
        return 'severity-medium';
      case 'high':
        return 'severity-high';
      case 'critical':
        return 'severity-critical';
      default:
        return '';
    }
  }

  getStatusClass(status: string | null | undefined): string {
    switch (status) {
      case 'submitted':
        return 'status-submitted';
      case 'under_review':
        return 'status-under-review';
      case 'resolved':
        return 'status-resolved';
      case 'dismissed':
        return 'status-dismissed';
      default:
        return '';
    }
  }

  getStatusLabel(status: string | null | undefined): string {
    switch (status) {
      case 'submitted':
        return 'Submitted';
      case 'under_review':
        return 'Under Review';
      case 'resolved':
        return 'Resolved';
      case 'dismissed':
        return 'Dismissed';
      default:
        return status || '—';
    }
  }
}
